import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import ProfessionalLayout from '../components/ProfessionalLayout';
import api from '../lib/api';
import '../styles/professional-theme.css';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  CreditCard,
  Crown,
  Users,
  Package,
  Ship,
  Megaphone,
  ExternalLink,
  CheckCircle,
  Clock,
  XCircle,
  AlertCircle,
  RefreshCw,
  Building2,
  Zap,
  ArrowRight,
  Shield,
  Mail,
  MessageSquare,
} from 'lucide-react';

const FEATURE_KEYS = {
  email_notifications: 'featureEmail',
  qr_code: 'featureQr',
  basic_analytics: 'featureBasicAnalytics',
  advanced_analytics: 'featureAdvancedAnalytics',
  whatsapp: 'featureWhatsapp',
  broadcast: 'featureBroadcast',
  finance: 'featureFinance',
  whatsapp_ai_responses: 'featureWhatsappAi',
};

const StatusBadge = ({ status }) => {
  const config = {
    active: { cls: 'bg-green-100 text-green-700 border-green-200', icon: CheckCircle },
    trialing: { cls: 'bg-blue-100 text-blue-700 border-blue-200', icon: Clock },
    past_due: { cls: 'bg-amber-100 text-amber-700 border-amber-200', icon: AlertCircle },
    cancelled: { cls: 'bg-red-100 text-red-700 border-red-200', icon: XCircle },
    expired: { cls: 'bg-gray-100 text-gray-600 border-gray-200', icon: XCircle },
  };
  const c = config[status] || config.expired;
  const Icon = c.icon;
  return (
    <Badge variant="outline" className={`${c.cls} gap-1`}>
      <Icon size={12} />
      {status}
    </Badge>
  );
};

const UsageBar = ({ current, max, label, icon: Icon }) => {
  const { t } = useLanguage();
  const isUnlimited = max === -1;
  const pct = isUnlimited ? 0 : max > 0 ? Math.min((current / max) * 100, 100) : 0;
  const isWarning = !isUnlimited && pct > 80;
  const isDanger = !isUnlimited && pct >= 100;

  return (
    <div className="mb-5">
      <div className="flex items-center justify-between mb-1.5">
        <span className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wider">
          {Icon && <Icon size={13} />}
          {label}
        </span>
        <span className="text-sm font-semibold tabular-nums">
          {current} / {isUnlimited ? '∞' : max}
        </span>
      </div>
      <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${
            isUnlimited ? 'bg-blue-400' : isDanger ? 'bg-red-500' : isWarning ? 'bg-amber-500' : 'bg-green-500'
          }`}
          style={{ width: `${isUnlimited ? 15 : pct}%` }}
        />
      </div>
      {isDanger && (
        <p className="text-[10px] text-red-500 mt-1 font-medium">
          {t('usage')} 100% — {t('upgradePlan')}
        </p>
      )}
    </div>
  );
};

const SubscriptionsAdmin = () => {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const [subscription, setSubscription] = useState(null);
  const [planConfig, setPlanConfig] = useState(null);
  const [usage, setUsage] = useState(null);
  const [allPlans, setAllPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  // Super admin state
  const [allSubs, setAllSubs] = useState([]);
  const [stats, setStats] = useState(null);
  const [filterPlan, setFilterPlan] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [overrideCompany, setOverrideCompany] = useState(null);
  const [overridePlan, setOverridePlan] = useState('');
  const [overrideStatus, setOverrideStatus] = useState('');

  const isSuperAdmin = user?.role === 'super_admin';
  const isAdmin = user?.role === 'company_admin' || isSuperAdmin;

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [currentRes, usageRes, plansRes] = await Promise.all([
        api.get('/api/subscriptions/current'),
        api.get('/api/subscriptions/usage'),
        api.get('/api/subscriptions/plans'),
      ]);
      setSubscription(currentRes.data.subscription);
      setPlanConfig(currentRes.data.plan_config);
      setUsage(usageRes.data);
      setAllPlans(plansRes.data.plans || []);

      if (isSuperAdmin) {
        const allRes = await api.get('/api/subscriptions/admin/all');
        setAllSubs(allRes.data.subscriptions);
        setStats(allRes.data.stats);
      }
    } catch (err) {
      console.error('Failed to fetch subscription data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckout = async (plan) => {
    try {
      const res = await api.post('/api/subscriptions/checkout', {
        plan,
        success_url: `${window.location.origin}/subscriptions?success=true`,
        cancel_url: `${window.location.origin}/subscriptions?cancelled=true`,
      });
      if (res.data.url) window.location.href = res.data.url;
    } catch (err) {
      alert(err.response?.data?.detail || 'Stripe not configured');
    }
  };

  const handlePortal = async () => {
    try {
      const res = await api.post('/api/subscriptions/portal', {
        return_url: `${window.location.origin}/subscriptions`,
      });
      if (res.data.url) window.location.href = res.data.url;
    } catch (err) {
      alert(err.response?.data?.detail || 'Cannot open billing portal');
    }
  };

  const handleOverride = async () => {
    if (!overrideCompany) return;
    try {
      const body = {};
      if (overridePlan) body.plan = overridePlan;
      if (overrideStatus) body.status = overrideStatus;
      await api.put(`/api/subscriptions/admin/${overrideCompany}`, body);
      setOverrideCompany(null);
      setOverridePlan('');
      setOverrideStatus('');
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Override failed');
    }
  };

  const planNames = { starter: 'Starter', pro: 'Pro', enterprise: 'Enterprise' };
  const planColors = {
    starter: { bg: 'rgb(102 102 102 / 0.1)', color: '#666' },
    pro: { bg: 'rgb(232 89 12 / 0.1)', color: '#e8590c' },
    enterprise: { bg: 'rgb(26 26 26 / 0.08)', color: '#1a1a1a' },
  };

  const limits = planConfig?.limits || {};
  const currentPlanKey = subscription?.plan || 'starter';

  if (loading) {
    return (
      <ProfessionalLayout>
        <div className="flex items-center justify-center py-20">
          <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </ProfessionalLayout>
    );
  }

  return (
    <ProfessionalLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold">{t('planAndBilling')}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t('planAndBillingSubtitle')}</p>
        </div>

        {/* KPI Cards — Usage */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="analytics-kpi">
            <div className="analytics-kpi-header">
              <span className="analytics-kpi-label">{t('usersCount')}</span>
              <div className="analytics-kpi-icon" style={{ background: 'rgb(26 26 26 / 0.08)', color: '#1a1a1a' }}>
                <Users size={18} />
              </div>
            </div>
            <div className="analytics-kpi-value">{usage?.users || 0}</div>
            <div className="analytics-kpi-delta">
              {limits.max_users === -1
                ? t('unlimited')
                : `${t('ofLimit')} ${limits.max_users || '—'} max`}
              {' · '}{usage?.users || 0} {t('activeUsers')}
            </div>
          </div>

          <div className="analytics-kpi">
            <div className="analytics-kpi-header">
              <span className="analytics-kpi-label">{t('packagesMonth')}</span>
              <div className="analytics-kpi-icon" style={{ background: 'rgb(232 89 12 / 0.1)', color: '#e8590c' }}>
                <Package size={18} />
              </div>
            </div>
            <div className="analytics-kpi-value">{usage?.packages_this_month || 0}</div>
            <div className="analytics-kpi-delta">
              {limits.max_packages_per_month === -1
                ? t('unlimited')
                : `${t('ofLimit')} ${limits.max_packages_per_month || '—'} ${t('perMonth')}`}
              {' · '}{usage?.packages_total || 0} total
            </div>
          </div>

          <div className="analytics-kpi">
            <div className="analytics-kpi-header">
              <span className="analytics-kpi-label">{t('shipmentsMonth')}</span>
              <div className="analytics-kpi-icon" style={{ background: 'rgb(16 185 129 / 0.1)', color: '#10b981' }}>
                <Ship size={18} />
              </div>
            </div>
            <div className="analytics-kpi-value">{usage?.shipments_this_month || 0}</div>
            <div className="analytics-kpi-delta">
              {limits.max_shipments_per_month === -1
                ? t('unlimited')
                : `${t('ofLimit')} ${limits.max_shipments_per_month || '—'} ${t('perMonth')}`}
              {' · '}{t('thisMonth')}
            </div>
          </div>

          <div className="analytics-kpi">
            <div className="analytics-kpi-header">
              <span className="analytics-kpi-label">{t('broadcastsSent')}</span>
              <div className="analytics-kpi-icon" style={{ background: 'rgb(139 92 246 / 0.1)', color: '#8b5cf6' }}>
                <Megaphone size={18} />
              </div>
            </div>
            <div className="analytics-kpi-value">{usage?.broadcasts_this_month || 0}</div>
            <div className="analytics-kpi-delta">
              {usage?.broadcasts_total || 0} {t('totalSent')}
            </div>
          </div>
        </div>

        {/* Plan + Usage Row */}
        <div className="grid md:grid-cols-3 gap-5">
          {/* Current Plan Card */}
          <Card className="analytics-card md:col-span-1">
            <CardContent className="p-6">
              {subscription ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-10 h-10 rounded-xl grid place-items-center"
                        style={planColors[currentPlanKey] || planColors.starter}
                      >
                        <Crown size={20} />
                      </div>
                      <div>
                        <div className="text-xl font-bold">{planNames[currentPlanKey]}</div>
                        <div className="text-xs text-muted-foreground">
                          {planConfig?.price_monthly_eur}&euro;{t('perMonth')}
                        </div>
                      </div>
                    </div>
                    <StatusBadge status={subscription.status} />
                  </div>

                  {subscription.current_period_end && (
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <Clock size={12} />
                      {t('renewsOn')}{' '}
                      {new Date(subscription.current_period_end).toLocaleDateString(
                        language === 'fr' ? 'fr-FR' : 'en-US'
                      )}
                    </div>
                  )}

                  {/* Features */}
                  <div>
                    <div className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground mb-2">
                      {t('planFeatures')}
                    </div>
                    <div className="space-y-1.5">
                      {(limits.features || []).map((f) => (
                        <div key={f} className="flex items-center gap-2 text-sm">
                          <CheckCircle size={14} className="text-green-500 shrink-0" />
                          <span>{t(FEATURE_KEYS[f] || f)}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Actions */}
                  {isAdmin && (
                    <div className="flex gap-2 pt-2">
                      <Button size="sm" onClick={() => handleCheckout('pro')} className="gap-1.5">
                        <Zap size={14} />
                        {t('upgradePlan')}
                      </Button>
                      {subscription.stripe_customer_id && (
                        <Button size="sm" variant="outline" onClick={handlePortal} className="gap-1.5">
                          <CreditCard size={14} />
                          {t('manageBilling')}
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8">
                  <CreditCard size={40} className="mx-auto text-muted-foreground mb-3" />
                  <div className="font-semibold mb-1">{t('noSubscription')}</div>
                  <p className="text-sm text-muted-foreground mb-4">{t('noSubscriptionDesc')}</p>
                  {isAdmin && (
                    <Button size="sm" onClick={() => handleCheckout('starter')} className="gap-1.5">
                      <Zap size={14} />
                      {t('changePlan')}
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Usage Progress Card */}
          <Card className="analytics-card md:col-span-2">
            <CardHeader className="analytics-card-header">
              <CardTitle className="analytics-card-title">{t('usageThisMonth')}</CardTitle>
            </CardHeader>
            <CardContent>
              <UsageBar
                current={usage?.users || 0}
                max={limits.max_users ?? 5}
                label={t('usersCount')}
                icon={Users}
              />
              <UsageBar
                current={usage?.packages_this_month || 0}
                max={limits.max_packages_per_month ?? 100}
                label={t('packagesMonth')}
                icon={Package}
              />
              <UsageBar
                current={usage?.shipments_this_month || 0}
                max={limits.max_shipments_per_month ?? 5}
                label={t('shipmentsMonth')}
                icon={Ship}
              />
              <UsageBar
                current={usage?.partners || 0}
                max={limits.max_partners ?? 3}
                label={t('partnersCount')}
                icon={Shield}
              />
              <UsageBar
                current={usage?.emails_this_month || 0}
                max={limits.max_emails_per_month ?? 0}
                label="Emails"
                icon={Mail}
              />
              <UsageBar
                current={usage?.whatsapp_this_month || 0}
                max={limits.max_whatsapp_per_month ?? 0}
                label="WhatsApp"
                icon={MessageSquare}
              />
              <UsageBar
                current={usage?.broadcasts_this_month || 0}
                max={limits.max_broadcasts_per_month ?? 0}
                label="Diffusions"
                icon={Megaphone}
              />
            </CardContent>
          </Card>
        </div>

        {/* Plans Comparison */}
        {allPlans.length > 0 && (
          <Card className="analytics-card">
            <CardHeader className="analytics-card-header">
              <CardTitle className="analytics-card-title">{t('comparePlans')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {allPlans.map((plan) => {
                  const isCurrent = currentPlanKey === plan.key;
                  const pc = planColors[plan.key] || planColors.starter;
                  return (
                    <div
                      key={plan.key}
                      className={`border rounded-xl p-5 transition-all ${
                        isCurrent
                          ? 'border-orange-300 bg-orange-50/30 ring-1 ring-orange-200'
                          : 'border-border hover:border-muted-foreground/30'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-3">
                        <div
                          className="w-8 h-8 rounded-lg grid place-items-center"
                          style={{ background: pc.bg, color: pc.color }}
                        >
                          <Crown size={16} />
                        </div>
                        <div>
                          <div className="font-bold">{plan.name}</div>
                          {isCurrent && (
                            <Badge variant="outline" className="text-[10px] bg-orange-100 text-orange-700 border-orange-200">
                              {t('currentPlan')}
                            </Badge>
                          )}
                        </div>
                      </div>

                      <div className="mb-4">
                        <span className="text-2xl font-bold">{plan.price_monthly_eur}&euro;</span>
                        <span className="text-sm text-muted-foreground">{t('perMonth')}</span>
                        <div className="text-xs text-muted-foreground">
                          {plan.price_yearly_eur}&euro;{t('perYear')}
                        </div>
                      </div>

                      <div className="space-y-2 text-sm mb-4">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">{t('usersCount')}</span>
                          <span className="font-medium">{plan.limits.max_users === -1 ? '∞' : plan.limits.max_users}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">{t('packages')}</span>
                          <span className="font-medium">{plan.limits.max_packages_per_month === -1 ? '∞' : plan.limits.max_packages_per_month}{t('perMonth')}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">{t('shipments')}</span>
                          <span className="font-medium">{plan.limits.max_shipments_per_month === -1 ? '∞' : plan.limits.max_shipments_per_month}{t('perMonth')}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">{t('partnersCount')}</span>
                          <span className="font-medium">{plan.limits.max_partners === -1 ? '∞' : plan.limits.max_partners}</span>
                        </div>
                      </div>

                      <div className="border-t pt-3 space-y-1.5">
                        {(plan.limits.features || []).map((f) => (
                          <div key={f} className="flex items-center gap-1.5 text-xs">
                            <CheckCircle size={12} className="text-green-500 shrink-0" />
                            <span>{t(FEATURE_KEYS[f] || f)}</span>
                          </div>
                        ))}
                      </div>

                      {isAdmin && !isCurrent && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="w-full mt-4 gap-1.5"
                          onClick={() => handleCheckout(plan.key)}
                        >
                          {t('changePlan')} <ArrowRight size={14} />
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Super Admin Section */}
        {isSuperAdmin && stats && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="analytics-kpi">
                <div className="analytics-kpi-header">
                  <span className="analytics-kpi-label">Total</span>
                  <div className="analytics-kpi-icon" style={{ background: 'rgb(26 26 26 / 0.08)', color: '#1a1a1a' }}>
                    <Building2 size={18} />
                  </div>
                </div>
                <div className="analytics-kpi-value">{stats.total}</div>
                <div className="analytics-kpi-delta">{t('allSubscriptions')}</div>
              </div>
              {Object.entries(stats.plan_counts).map(([plan, count]) => (
                <div key={plan} className="analytics-kpi">
                  <div className="analytics-kpi-header">
                    <span className="analytics-kpi-label">{planNames[plan] || plan}</span>
                    <div className="analytics-kpi-icon" style={planColors[plan] || planColors.starter}>
                      <Crown size={18} />
                    </div>
                  </div>
                  <div className="analytics-kpi-value">{count}</div>
                  <div className="analytics-kpi-delta">{t('subscription')}</div>
                </div>
              ))}
              <div className="analytics-kpi">
                <div className="analytics-kpi-header">
                  <span className="analytics-kpi-label">MRR</span>
                  <div className="analytics-kpi-icon" style={{ background: 'rgb(16 185 129 / 0.1)', color: '#10b981' }}>
                    <CreditCard size={18} />
                  </div>
                </div>
                <div className="analytics-kpi-value">{stats.mrr.toFixed(0)}&euro;</div>
                <div className="analytics-kpi-delta">{t('monthlyRevenue')}</div>
              </div>
            </div>

            {/* Subscriptions Table */}
            <Card className="analytics-card">
              <CardHeader className="analytics-card-header">
                <div className="flex items-center justify-between w-full">
                  <CardTitle className="analytics-card-title flex items-center gap-2">
                    <Building2 size={16} />
                    {t('allSubscriptions')}
                  </CardTitle>
                  <div className="flex gap-2">
                    <select
                      value={filterPlan}
                      onChange={(e) => setFilterPlan(e.target.value)}
                      className="border border-border rounded-lg px-2 py-1 text-xs bg-background"
                    >
                      <option value="">{language === 'fr' ? 'Tous les plans' : 'All plans'}</option>
                      <option value="starter">Starter</option>
                      <option value="pro">Pro</option>
                      <option value="enterprise">Enterprise</option>
                    </select>
                    <select
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value)}
                      className="border border-border rounded-lg px-2 py-1 text-xs bg-background"
                    >
                      <option value="">{language === 'fr' ? 'Tous' : 'All'}</option>
                      <option value="active">Active</option>
                      <option value="trialing">Trialing</option>
                      <option value="past_due">Past Due</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="analytics-table w-full">
                    <thead>
                      <tr>
                        <th>{language === 'fr' ? 'Entreprise' : 'Company'}</th>
                        <th>Plan</th>
                        <th>{t('status')}</th>
                        <th>{language === 'fr' ? 'Période' : 'Period'}</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allSubs
                        .filter((s) => {
                          if (filterPlan && s.plan !== filterPlan) return false;
                          if (filterStatus && s.status !== filterStatus) return false;
                          return true;
                        })
                        .map((sub) => (
                          <tr key={sub.id || sub.company_id}>
                            <td className="font-medium">{sub.company_name}</td>
                            <td>
                              <Badge variant="outline">{planNames[sub.plan] || sub.plan}</Badge>
                            </td>
                            <td><StatusBadge status={sub.status} /></td>
                            <td className="text-muted-foreground text-xs">
                              {sub.current_period_end
                                ? new Date(sub.current_period_end).toLocaleDateString(
                                    language === 'fr' ? 'fr-FR' : 'en-US'
                                  )
                                : '—'}
                            </td>
                            <td>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                  setOverrideCompany(sub.company_id);
                                  setOverridePlan(sub.plan);
                                  setOverrideStatus(sub.status);
                                }}
                              >
                                {t('overridePlan')}
                              </Button>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Override Modal */}
            {overrideCompany && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                <div className="bg-card rounded-xl p-6 w-full max-w-md mx-4 border">
                  <h3 className="text-lg font-bold mb-4">{t('overridePlan')}</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">Plan</label>
                      <select
                        value={overridePlan}
                        onChange={(e) => setOverridePlan(e.target.value)}
                        className="w-full border border-border rounded-lg px-3 py-2 bg-background"
                      >
                        <option value="starter">Starter</option>
                        <option value="pro">Pro</option>
                        <option value="enterprise">Enterprise</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">{t('status')}</label>
                      <select
                        value={overrideStatus}
                        onChange={(e) => setOverrideStatus(e.target.value)}
                        className="w-full border border-border rounded-lg px-3 py-2 bg-background"
                      >
                        <option value="active">Active</option>
                        <option value="trialing">Trialing</option>
                        <option value="past_due">Past Due</option>
                        <option value="cancelled">Cancelled</option>
                        <option value="expired">Expired</option>
                      </select>
                    </div>
                    <div className="flex gap-3 justify-end">
                      <Button variant="outline" onClick={() => setOverrideCompany(null)}>
                        {t('cancel')}
                      </Button>
                      <Button onClick={handleOverride}>{t('save')}</Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </ProfessionalLayout>
  );
};

export default SubscriptionsAdmin;
